import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { PropertyInfoPanel } from './PropertyInfoPanel';

// Module-level literals — schema extractor follows one level (same shape as PropertyInfoPanel).
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone',
  fields: {
    text: {
      label: 'Phone',
      type: 'text',
      defaultField: 'property_call_tracking_phone',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

/**
 * Contact Us — lead form + property info tiles.
 * Block wiring matches Preferred Theme: one live EditableToken with a
 * static path wrapping useBlockSlot (no hidden duplicate for form_block).
 */
export function ContactUs() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const formSlot = useBlockSlot('contactus.form_block', {
    defaultValue: 'multihub-lead-form',
  });

  return (
    <section ref={sectionRef as React.RefObject<HTMLElement>} className="reveal-fade-up cu-section">
      <div className="cu-container">
        <div className="cu-grid">
          <div className="cu-form-col">
            <EditableToken path="contactus.form_block" as="div">
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : null}
            </EditableToken>
          </div>

          <div className="cu-info-col">
            <PropertyInfoPanel prefix="contactus" />
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — PropertyInfoPanel uses dynamic `${prefix}.*` paths. */}
      <EditableToken path="contactus.visit_label" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactus.call_label" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableSlotGroup groupPath="contactus.address" definition={addressLinkDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="contactus.phone" definition={phoneLinkDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableToken path="contactus.hours_label" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>

      <style>{`
        .cu-section {
          padding: 2.5rem 0 3.5rem;
          background: var(--vibe-color-background);
        }
        @media (min-width: 768px) {
          .cu-section { padding: 3rem 0 4rem; }
        }
        .cu-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .cu-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .cu-container { padding: 0 2rem; } }

        .cu-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        @media (min-width: 1024px) {
          .cu-grid {
            grid-template-columns: 7fr 5fr;
            gap: 2.5rem;
            align-items: start;
          }
        }

        .cu-info-col .cf-info-cards {
          flex-direction: column;
          flex-wrap: nowrap;
          gap: 0.75rem;
        }
        .cu-info-col .cf-card {
          flex: none;
          width: 100%;
        }

        @media (max-width: 1023px) {
          .cu-info-col .cf-info-cards {
            flex-direction: row;
            flex-wrap: wrap;
          }
          .cu-info-col .cf-card {
            flex: 1 1 140px;
          }
        }

        .cu-form-col { position: relative; }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactUs',
  displayName: 'Contact Us',
  tokens: {
    'contactus.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactus.visit_label': {
      label: 'Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'contactus.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'contactus.hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'contactus.address': addressLinkDef,
    'contactus.phone': phoneLinkDef,
  },
  collectionBindings: {
    'contactus.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ContactUs as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactUs;
